import * as React from "react";
import { Alert, Box } from "@mui/material";
import {
  GoogleMap,
  Marker,
  useLoadScript,

} from "@react-google-maps/api";
import { GMAP_API_KEY } from "../../configs/api";

export interface MapMarker {
  id: string | number;
  position: { lat: number; lng: number; };
  label?: string;
  onClick?: () => void;
  icon?: string | google.maps.Icon | google.maps.Symbol;
}

interface MapBaseProps {
  center?: { lat: number; lng: number; };
  zoom?: number;
  containerStyle?: React.CSSProperties;
  markers?: MapMarker[];
  children?: React.ReactNode;
  onMapLoad?: (map: google.maps.Map) => void;
  options?: google.maps.MapOptions;
  mapId?: string;
}

const defaultContainerStyle: React.CSSProperties = {
  width: "100%",
  height: "300px",
};

const defaultOptions: google.maps.MapOptions = {
  fullscreenControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  zoomControl: true,
  scrollwheel: false,
};

const MapBase: React.FC<MapBaseProps> = ({
  center = { lat: 0, lng: 0 },
  zoom = 14,
  containerStyle = defaultContainerStyle,
  markers = [],
  children,
  onMapLoad,
  options = defaultOptions,
  mapId,
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GMAP_API_KEY,
    libraries: ["places", "geometry"],
  });

  if (loadError) {
    return (
      <Alert severity="error">Map cannot be loaded right now, sorry.</Alert>
    );
  }

  if (!isLoaded) {
    return <Box>Loading map...</Box>;
  }

  const mergedOptions = { ...defaultOptions, ...options };

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        width: "100%",
        height: "300px",
        "& .gm-style": {
          borderRadius: 2,
        },
      }}
    >
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={zoom}
        options={mergedOptions}
        onLoad={onMapLoad}
        id={mapId}
      >
        {markers.map((marker) => (
          <Marker
            key={marker.id}
            position={marker.position}
            label={marker.label}
            onClick={marker.onClick}
            icon={marker.icon}
          />
        ))}
        {children}
      </GoogleMap>
    </Box>
  );
};

export default MapBase;